// Globals
import React from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Form, FormItem, IFormInstance, ModalSize } from '@/components';
import { StyledBlockText, StyledButton, StyledText, StyledTitle } from '../Styled';
import { StyledInput, StyledModal } from './ResetPasswordModalStyles';
import { CheckmarkCircleIcon } from '@/icons';

// Helpers
import { AuthFieldsNames, TResetPasswordFormValues } from '../../helpers/types';
import { useResetPasswordRules } from '../../hooks/useValidationRules';

interface IResetPasswordModal {
  formInstance: IFormInstance;
  handleSubmit: (values: TResetPasswordFormValues) => void;
  onClose: () => void;
  isLoading: boolean;
  isOpened: boolean;
}

const ResetPasswordModal = ({ formInstance, handleSubmit, isLoading, isOpened, onClose }: IResetPasswordModal) => {
  const { t } = useTranslation(['auth', 'common']);
  const rules = useResetPasswordRules();

  return (
    <StyledModal visible={isOpened} onClose={onClose} onCancel={onClose} size={ModalSize.LARGE}>
      <StyledTitle>
        <CheckmarkCircleIcon />
        {t('auth_entered_message')}
      </StyledTitle>
      <StyledText>{t('auth_set_password_message')}</StyledText>

      <Form onFinish={handleSubmit} form={formInstance}>
        <FormItem
          name={AuthFieldsNames.PASSWORD}
          label={t('auth_new_password')}
          rules={rules[AuthFieldsNames.PASSWORD]}
        >
          <StyledInput placeholder={t('common_placeholder_input', { ns: 'common' })} />
        </FormItem>

        <FormItem
          name={AuthFieldsNames.CONFIRM_PASSWORD}
          label={t('auth_repeat_password')}
          rules={[
            ...rules[AuthFieldsNames.CONFIRM_PASSWORD],
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue(AuthFieldsNames.PASSWORD) === value) {
                  return Promise.resolve();
                }

                return Promise.reject(
                  t('validation_message_confirm_password', {
                    ns: 'common',
                  }),
                );
              },
            }),
          ]}
        >
          <StyledInput placeholder={t('common_placeholder_input', { ns: 'common' })} />
        </FormItem>

        <StyledBlockText>{t('auth_set_password_requirements')}</StyledBlockText>
        <StyledText>{t('auth_set_password_requirements_list')}</StyledText>
        <StyledButton type="primary" htmlType="submit" loading={isLoading}>
          {t('auth_set_password_save')}
        </StyledButton>
      </Form>
    </StyledModal>
  );
};

export default ResetPasswordModal;
